import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import categoryService from "../../repository/categoryRepository";

const EditMainCategoryModal = ({
  open,
  onClose,
  onSave,
  initialValues = {},
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [mainCategoryName, setMainCategoryName] = useState("");
  const [mainCategoryDescription, setMainCategoryDescription] = useState("");
  const [mainCategoryIcon, setMainCategoryIcon] = useState(null);
  const [mainCategoryIconName, setMainCategoryIconName] = useState("");
  const [mainCategoryId, setMainCategoryId] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    setMainCategoryName(initialValues.name || "");
    setMainCategoryDescription(initialValues.description || "");
    setMainCategoryIcon(null);
    setMainCategoryId(initialValues.mainCategoryId || "");
    setMainCategoryIconName("");
  }, [initialValues]);

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    setMainCategoryIcon(file);
    setMainCategoryIconName(file ? file.name : "");
  };

  const handleSaveChanges = async () => {
    try {
      const formData = new FormData();
      formData.append("mainCategoryName", mainCategoryName);
      formData.append("description", mainCategoryDescription);
      formData.append("mainCategoryId", mainCategoryId);
      if (mainCategoryIcon) {
        formData.append("imageURL", mainCategoryIcon);
        formData.append("isImage", true);
      }

      const response = await categoryService.editMainCategory(formData);

      // Handle specific status codes
      if (response.success) {
        setSnackbarMessage("Category updated successfully!");
        setSnackbarSeverity("success");
        if (typeof onSave === "function") {
          onSave(response.data);
        } else {
          console.error("onSave is not a function");
        }
        onClose();
      } else if (response.status === 400) {
        setSnackbarMessage("Please try again.");
        setSnackbarSeverity("warning");
      } else if (response.status === 500) {
        setSnackbarMessage("Server error. Please try again later.");
        setSnackbarSeverity("error");
      } else {
        setSnackbarMessage("Error saving category.");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      setSnackbarMessage("Error saving category: " + error.message);
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor:
              colors.background || theme.palette.background.default,
            color: colors.text || theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary || theme.palette.primary.main,
            color:
              theme.palette.mode === "light"
                ? "black"
                : colors.light || theme.palette.grey[100],
            position: "relative",
          }}
        >
          Edit Main Category
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.grey || theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            type="text"
            fullWidth
            variant="outlined"
            value={mainCategoryName}
            onChange={(e) => setMainCategoryName(e.target.value)}
            sx={{
              marginBottom: 2,
              input: { color: colors.text || theme.palette.text.primary },
            }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={mainCategoryDescription}
            onChange={(e) => setMainCategoryDescription(e.target.value)}
            sx={{
              marginBottom: 2,
              input: { color: colors.text || theme.palette.text.primary },
            }}
          />
          <Box mb={2}>
            <Button
              variant="contained"
              component="label"
              color="secondary"
              startIcon={<CloudUploadIcon />}
              sx={{
                mb: "10px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.secondary || theme.palette.secondary.main
                    : colors.secondaryLight || theme.palette.secondary.light,
                color: "black",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? colors.secondary || theme.palette.secondary.main
                      : colors.secondaryLight || theme.palette.secondary.light,
                },
              }}
            >
              Upload Icon
              <input
                type="file"
                name="categoryIcon"
                hidden
                onChange={handleIconUpload}
              />
            </Button>
            {mainCategoryIconName && (
              <Typography
                variant="body2"
                color={(theme) => theme.palette.text.primary}
              >
                Uploaded: {mainCategoryIconName}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveChanges}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.greenAccent || theme.palette.success.main
                  : colors.blueAccent || theme.palette.primary.main,
              color: "black",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.greenAccent || theme.palette.success.main
                    : colors.blueAccent || theme.palette.primary.main,
              },
            }}
          >
            Update Main Category
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          margin: 2,
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.snackbarLightBackground
              : colors.snackbarDarkBackground,
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{
            backgroundColor:
              snackbarSeverity === "success"
                ? colors.greenAccent
                : colors.redAccent || theme.palette.error.main,
            color:
              theme.palette.mode === "light"
                ? colors.snackbarLightText
                : colors.light || theme.palette.grey[100],
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditMainCategoryModal;
